import { girarEmTorno, limitar, somar, subtrair, vetor, type Vetor3 } from '../geometria/vetor3'
import { alturaDeApoioExata, INCLINACAO_CAPOTADO_RAD } from './configuracaoVeiculo'
import {
  criarInterpoladorMonotono,
  type InterpoladorMonotono,
} from '../geometria/hermiteMonotona'
import {
  ESPESSURA_PAINEL_TETO,
  type ConfiguracaoSrs,
  type PerfilVeiculo,
} from './perfilVeiculo'
import type { TipoAlvoImpacto } from './alvosImpacto'

/**
 * Timelines didáticas determinísticas por tipo de colisão.
 *
 * NÃO é simulação biomecânica nem física de corpo mole: são quadros-chave
 * escolhidos para tornar legível o mesmo princípio nos quatro casos — o veículo
 * muda de velocidade, o ocupante continua o movimento que tinha, e a retenção é
 * o que acopla um ao outro. Qualquer número aqui é recurso de ensino, não medida.
 */
export type TipoColisao =
  | 'frontal'
  | 'lateral'
  | 'traseira'
  | 'tombamento'
  | 'capotamento'
  | 'lateralComCapotamento'
  | 'frontalComTraseira'
  | 'abalroamento'
  | 'lateralContraObjeto'

export const TIPOS_COLISAO: readonly TipoColisao[] = [
  'frontal',
  'lateral',
  'traseira',
  'frontalComTraseira',
  'abalroamento',
  'lateralContraObjeto',
  'tombamento',
  'capotamento',
  'lateralComCapotamento',
]

export interface QuadroCinematica {
  tempo: number
  fase: string
  descricao: string
  /**
   * Posição do veículo em coordenadas de mundo.
   *
   * Os quadros-chave declaram a FORMA do caminho — a polilinha e as curvas dela.
   * Onde o veículo está em cada instante é DERIVADO da curva de velocidade
   * (`velocidadeVeiculo` × `velocidadeImpactoKmh`), e não lido do quadro-chave
   * mais próximo: os dois eram verdades separadas que discordavam, e quem
   * mandava era a posição. A frontal declarava 50 km/h e o veículo andava a 42.
   */
  posicaoVeiculo: Vetor3
  /** rotação do veículo em radianos: no capotamento é a rolagem em torno de +Z */
  rotacaoVeiculoRad: Vetor3
  /** velocidade de translação do veículo, normalizada em 0..1 */
  velocidadeVeiculo: number
  /** deslocamento do ocupante em relação ao veículo, no referencial do veículo */
  deslocamentoRelativoOcupante: Vetor3
  /** inclinação do tronco para frente (+) ou para trás (−) */
  inclinacaoTroncoRad: number
  /**
   * inclinação do tronco para o lado: positivo tomba para −X (lado do
   * passageiro), negativo para +X (lado do motorista)
   */
  inclinacaoLateralTroncoRad: number
  inclinacaoCabecaRad: number
  cintoAtivo: boolean
  pretensionadorAtivo: boolean
  /** 0 = módulo fechado, 1 = totalmente expandido */
  expansaoAirbag: number
}

type Chave = Omit<QuadroCinematica, 'cintoAtivo' | 'pretensionadorAtivo'>

/**
 * Um SEGUNDO contato externo no mesmo evento — a reação em cadeia do
 * engavetamento (frontal seguida de traseira).
 *
 * Não declara o instante nem a região do dano: os dois são DERIVADOS, pela
 * mesma razão que `posicaoVeiculo` já é derivada da curva de velocidade e não
 * lida do quadro-chave mais próximo (ver comentário em `QuadroCinematica`) —
 * um número declarado e a curva de velocidade são duas verdades sobre o
 * mesmo fato, e elas discordam. O instante sai de `instantesDeContato`
 * (o momento em que a velocidade volta a crescer depois de ter caído) e a
 * região sai da direção do impacto (`regiaoPeloImpacto`, em danoVeicular.ts).
 */
export interface ImpactoSecundario {
  /** como o painel e o rótulo 3D nomeiam este segundo contato */
  nome: string
  /** de onde vem o segundo impacto, em coordenadas do veículo */
  direcaoImpacto: Vetor3
  /** o que atinge o veículo pela segunda vez; numa reação em cadeia é sempre outro veículo */
  alvo: TipoAlvoImpacto
  srsAcionados: readonly (keyof ConfiguracaoSrs)[]
  acionaPretensionador: boolean
}

export interface DefinicaoColisao {
  id: TipoColisao
  nome: string
  /** o princípio que esta sequência serve para mostrar */
  descricao: string
  /** de onde vem o impacto, em coordenadas do veículo; nulo no capotamento */
  direcaoImpacto: Vetor3 | null
  /**
   * Dispositivos SRS que ESTE tipo de colisão aciona, dentre os que o perfil
   * declara. Lista por tipo justamente para não sugerir que todo evento dispara
   * tudo: na traseira não dispara nenhum, e o protagonista é o apoio de cabeça.
   */
  srsAcionados: readonly (keyof ConfiguracaoSrs)[]
  acionaPretensionador: boolean
  /**
   * A partir de quando a retenção entra em carga.
   * Por tipo porque no capotamento o cinto é a única coisa que segura o ocupante
   * desde o início da rolagem — ler "sem carga" com o veículo sobre a lateral
   * ensina o contrário do que a cena mostra.
   */
  tempoRetencao: number
  /** a sequência envolve rolagem do veículo, e a cena precisa reposicioná-lo */
  comRolagem: boolean
  /**
   * Velocidade do veículo no início da sequência, em km/h.
   *
   * É o ÚNICO número real declarado aqui: a duração do evento é derivada dele e
   * do caminho que os quadros já desenham (`duracaoRealS`). Serve a duas coisas
   * que a escala normalizada 0..1 não sabe responder — quanta energia a carga
   * solta carrega (`energiaDeImpacto`) e quanto tempo a gravidade tem para agir
   * sobre ela. Recurso de ensino, como todo número deste arquivo: é a velocidade
   * típica do cenário didático, não medição de um acidente.
   */
  velocidadeImpactoKmh: number
  /**
   * Velocidade típica da rolagem, em graus por segundo; 0 quando não há rolagem.
   *
   * O capotamento precisa deste segundo número porque, ali, o que dita a duração
   * do evento é a rotação e não o caminho: a translação desenhada daria menos de
   * três décimos de segundo para uma meia-volta completa, o que nenhuma carga
   * solta atravessaria de forma reconhecível.
   */
  velocidadeRolagemGrausPorS: number
  quadros: readonly Chave[]
  /** segundo contato externo na mesma linha do tempo; ausente na maioria dos tipos */
  impactoSecundario?: ImpactoSecundario
}

const z = (valor: number): Vetor3 => vetor(0, 0, valor)
const nulo: Vetor3 = vetor(0, 0, 0)

/** Quadros-chave da sequência frontal (§11 do PROMPT2). */
const FRONTAL: readonly Chave[] = [
  {
    tempo: 0,
    fase: 'Movimento',
    descricao: 'Veículo e vítima deslocam-se juntos, na mesma velocidade.',
    posicaoVeiculo: z(-2.6),
    rotacaoVeiculoRad: nulo,
    velocidadeVeiculo: 1,
    deslocamentoRelativoOcupante: nulo,
    inclinacaoTroncoRad: 0,
    inclinacaoLateralTroncoRad: 0,
    inclinacaoCabecaRad: 0.05,
    expansaoAirbag: 0,
  },
  {
    tempo: 0.15,
    fase: 'Contato',
    descricao: 'Início do contato e da desaceleração da estrutura dianteira.',
    posicaoVeiculo: z(-1.35),
    rotacaoVeiculoRad: nulo,
    velocidadeVeiculo: 0.96,
    deslocamentoRelativoOcupante: nulo,
    inclinacaoTroncoRad: 0,
    inclinacaoLateralTroncoRad: 0,
    inclinacaoCabecaRad: 0.05,
    expansaoAirbag: 0,
  },
  {
    tempo: 0.25,
    fase: 'Desaceleração',
    descricao: 'O veículo desacelera fortemente; a zona de absorção trabalha e, quando o perfil o tem, o airbag frontal já encheu.',
    posicaoVeiculo: z(-0.62),
    rotacaoVeiculoRad: nulo,
    velocidadeVeiculo: 0.52,
    deslocamentoRelativoOcupante: z(0.05),
    inclinacaoTroncoRad: 0.06,
    inclinacaoLateralTroncoRad: 0,
    inclinacaoCabecaRad: 0.08,
    expansaoAirbag: 1,
  },
  {
    tempo: 0.35,
    fase: 'Movimento relativo',
    descricao: 'O ocupante continua o deslocamento para frente em relação ao veículo.',
    posicaoVeiculo: z(-0.16),
    rotacaoVeiculoRad: nulo,
    velocidadeVeiculo: 0.16,
    deslocamentoRelativoOcupante: z(0.16),
    inclinacaoTroncoRad: 0.2,
    inclinacaoLateralTroncoRad: 0,
    inclinacaoCabecaRad: 0.26,
    expansaoAirbag: 1,
  },
  {
    tempo: 0.45,
    fase: 'Retenção',
    descricao: 'Cinto e pretensionador em evidência: é a retenção que acopla o corpo ao veículo.',
    posicaoVeiculo: nulo,
    rotacaoVeiculoRad: nulo,
    velocidadeVeiculo: 0,
    deslocamentoRelativoOcupante: z(0.26),
    inclinacaoTroncoRad: 0.34,
    inclinacaoLateralTroncoRad: 0,
    inclinacaoCabecaRad: 0.44,
    expansaoAirbag: 1,
  },
  {
    tempo: 0.55,
    fase: 'SRS',
    descricao: 'Cinto e airbag em carga: quando configurado no perfil, o airbag frontal amortece a cabeça e já começa a esvaziar pelos respiros.',
    posicaoVeiculo: nulo,
    rotacaoVeiculoRad: nulo,
    velocidadeVeiculo: 0,
    deslocamentoRelativoOcupante: z(0.32),
    inclinacaoTroncoRad: 0.44,
    inclinacaoLateralTroncoRad: 0,
    inclinacaoCabecaRad: 0.58,
    expansaoAirbag: 0.85,
  },
  {
    tempo: 0.7,
    fase: 'Deslocamento máximo',
    descricao: 'Maior deslocamento relativo do ocupante; cabeça e tronco à frente.',
    posicaoVeiculo: nulo,
    rotacaoVeiculoRad: nulo,
    velocidadeVeiculo: 0,
    deslocamentoRelativoOcupante: z(0.38),
    inclinacaoTroncoRad: 0.55,
    inclinacaoLateralTroncoRad: 0,
    inclinacaoCabecaRad: 0.74,
    expansaoAirbag: 0.65,
  },
  {
    tempo: 0.85,
    fase: 'Retorno',
    descricao: 'Retorno e acomodação simplificados contra o banco.',
    posicaoVeiculo: nulo,
    rotacaoVeiculoRad: nulo,
    velocidadeVeiculo: 0,
    deslocamentoRelativoOcupante: z(0.2),
    inclinacaoTroncoRad: 0.3,
    inclinacaoLateralTroncoRad: 0,
    inclinacaoCabecaRad: 0.3,
    expansaoAirbag: 0.45,
  },
  {
    tempo: 1,
    fase: 'Posição final',
    descricao: 'Posição final para análise. Não representa medida biomecânica.',
    posicaoVeiculo: nulo,
    rotacaoVeiculoRad: nulo,
    velocidadeVeiculo: 0,
    deslocamentoRelativoOcupante: z(0.14),
    inclinacaoTroncoRad: 0.2,
    inclinacaoLateralTroncoRad: 0,
    inclinacaoCabecaRad: 0.18,
    expansaoAirbag: 0.3,
  },
]

/**
 * Impacto lateral do lado do motorista.
 *
 * Doutrina: lado do motorista é o lado em que ele dirige — na maquete, +X.
 * O impacto chega por +X, o veículo é empurrado para −X e o ocupante fica para
 * trás, ou seja, desloca-se para +X em relação ao veículo: vai ao encontro da
 * lateral que está entrando.
 * É o oposto da frontal, onde o corpo se afasta da estrutura que colapsa — aqui
 * ele vai na direção dela, e é por isso que a distância lateral disponível é tão
 * menor que a frontal.
 *
 * A amplitude para no ponto em que o ombro encosta na chapa lateral. §21 proíbe
 * deformar a carroceria, então empurrar o corpo além disso só o faria atravessar
 * a porta — o aluno leria defeito de desenho, não aproximação do corpo à estrutura.
 */
const LATERAL: readonly Chave[] = [
  {
    tempo: 0,
    fase: 'Antes do impacto',
    descricao: 'Veículo e ocupante parados. O impacto vem pela lateral do motorista.',
    posicaoVeiculo: nulo,
    rotacaoVeiculoRad: nulo,
    velocidadeVeiculo: 0,
    deslocamentoRelativoOcupante: nulo,
    inclinacaoTroncoRad: 0,
    inclinacaoLateralTroncoRad: 0,
    inclinacaoCabecaRad: 0.05,
    expansaoAirbag: 0,
  },
  {
    tempo: 0.12,
    fase: 'Contato',
    descricao: 'Contato na lateral. Não há zona de absorção longitudinal para trabalhar aqui.',
    posicaoVeiculo: vetor(-0.04, 0, 0),
    rotacaoVeiculoRad: nulo,
    velocidadeVeiculo: 0.3,
    deslocamentoRelativoOcupante: nulo,
    inclinacaoTroncoRad: 0,
    inclinacaoLateralTroncoRad: -0.02,
    inclinacaoCabecaRad: 0.05,
    expansaoAirbag: 0,
  },
  {
    tempo: 0.26,
    fase: 'Aceleração lateral',
    descricao: 'O veículo é acelerado para o lado oposto ao impacto; o ocupante fica para trás.',
    posicaoVeiculo: vetor(-0.22, 0, 0),
    rotacaoVeiculoRad: nulo,
    velocidadeVeiculo: 0.72,
    deslocamentoRelativoOcupante: vetor(0.1, 0, 0),
    inclinacaoTroncoRad: 0,
    inclinacaoLateralTroncoRad: -0.14,
    inclinacaoCabecaRad: 0.06,
    expansaoAirbag: 0,
  },
  {
    tempo: 0.4,
    fase: 'SRS lateral',
    descricao:
      'Quando o perfil os declara, airbag lateral e de cortina expandem nesta janela — do lado do impacto.',
    posicaoVeiculo: vetor(-0.4, 0, 0),
    rotacaoVeiculoRad: nulo,
    velocidadeVeiculo: 0.62,
    deslocamentoRelativoOcupante: vetor(0.12, 0, 0),
    inclinacaoTroncoRad: 0,
    inclinacaoLateralTroncoRad: -0.2,
    inclinacaoCabecaRad: 0.08,
    expansaoAirbag: 0.9,
  },
  {
    tempo: 0.58,
    fase: 'Deslocamento máximo',
    descricao:
      'Maior aproximação entre o tronco e a lateral. O cinto de três pontos retém pouco o movimento lateral.',
    posicaoVeiculo: vetor(-0.56, 0, 0),
    rotacaoVeiculoRad: nulo,
    velocidadeVeiculo: 0.34,
    deslocamentoRelativoOcupante: vetor(0.145, 0, 0),
    inclinacaoTroncoRad: 0,
    inclinacaoLateralTroncoRad: -0.24,
    inclinacaoCabecaRad: 0.08,
    expansaoAirbag: 1,
  },
  {
    tempo: 0.8,
    fase: 'Retorno',
    descricao: 'O veículo desacelera e o tronco retorna parcialmente.',
    posicaoVeiculo: vetor(-0.66, 0, 0),
    rotacaoVeiculoRad: nulo,
    velocidadeVeiculo: 0.1,
    deslocamentoRelativoOcupante: vetor(0.1, 0, 0),
    inclinacaoTroncoRad: 0,
    inclinacaoLateralTroncoRad: -0.15,
    inclinacaoCabecaRad: 0.06,
    expansaoAirbag: 0.55,
  },
  {
    tempo: 1,
    fase: 'Posição final',
    descricao: 'Posição final para análise. Não representa medida biomecânica.',
    posicaoVeiculo: vetor(-0.7, 0, 0),
    rotacaoVeiculoRad: nulo,
    velocidadeVeiculo: 0,
    deslocamentoRelativoOcupante: vetor(0.07, 0, 0),
    inclinacaoTroncoRad: 0,
    inclinacaoLateralTroncoRad: -0.1,
    inclinacaoCabecaRad: 0.05,
    expansaoAirbag: 0.3,
  },
]

/**
 * Impacto traseiro.
 *
 * Aqui o veículo é ACELERADO, não desacelerado: parte do repouso e é empurrado
 * para frente. O ocupante fica para trás em relação ao veículo — para −Z — e a
 * cabeça, que nada empurra, fica ainda mais para trás que o tronco. É essa
 * defasagem entre tronco e cabeça que o apoio de cabeça existe para limitar, e é
 * o que a sequência serve para mostrar. Nenhum airbag é acionado.
 */
const TRASEIRA: readonly Chave[] = [
  {
    tempo: 0,
    fase: 'Antes do impacto',
    descricao: 'Veículo parado. O impacto vem pela traseira.',
    posicaoVeiculo: nulo,
    rotacaoVeiculoRad: nulo,
    velocidadeVeiculo: 0,
    deslocamentoRelativoOcupante: nulo,
    inclinacaoTroncoRad: 0,
    inclinacaoLateralTroncoRad: 0,
    inclinacaoCabecaRad: 0.05,
    expansaoAirbag: 0,
  },
  {
    tempo: 0.14,
    fase: 'Contato',
    descricao: 'Contato traseiro; a zona de absorção traseira começa a trabalhar.',
    posicaoVeiculo: z(0.06),
    rotacaoVeiculoRad: nulo,
    velocidadeVeiculo: 0.28,
    deslocamentoRelativoOcupante: nulo,
    inclinacaoTroncoRad: 0,
    inclinacaoLateralTroncoRad: 0,
    inclinacaoCabecaRad: 0.04,
    expansaoAirbag: 0,
  },
  {
    tempo: 0.28,
    fase: 'Aceleração do veículo',
    descricao: 'O veículo é acelerado para frente pelo impacto; o banco empurra o tronco.',
    posicaoVeiculo: z(0.34),
    rotacaoVeiculoRad: nulo,
    velocidadeVeiculo: 0.78,
    deslocamentoRelativoOcupante: z(-0.07),
    inclinacaoTroncoRad: -0.06,
    inclinacaoLateralTroncoRad: 0,
    inclinacaoCabecaRad: -0.16,
    expansaoAirbag: 0,
  },
  {
    tempo: 0.44,
    fase: 'Defasagem cabeça-tronco',
    descricao:
      'O encosto acelera o tronco, mas a cabeça só é acelerada quando encontra o apoio: é a defasagem que produz a lesão cervical.',
    posicaoVeiculo: z(0.72),
    rotacaoVeiculoRad: nulo,
    velocidadeVeiculo: 0.88,
    deslocamentoRelativoOcupante: z(-0.12),
    inclinacaoTroncoRad: -0.1,
    inclinacaoLateralTroncoRad: 0,
    inclinacaoCabecaRad: -0.44,
    expansaoAirbag: 0,
  },
  {
    tempo: 0.6,
    fase: 'Extensão máxima',
    descricao:
      'Máxima extensão da cabeça para trás. Um apoio de cabeça baixo ou afastado é o que permite que ela chegue até aqui.',
    posicaoVeiculo: z(1.12),
    rotacaoVeiculoRad: nulo,
    velocidadeVeiculo: 0.7,
    deslocamentoRelativoOcupante: z(-0.14),
    inclinacaoTroncoRad: -0.11,
    inclinacaoLateralTroncoRad: 0,
    inclinacaoCabecaRad: -0.56,
    expansaoAirbag: 0,
  },
  {
    tempo: 0.78,
    fase: 'Rebote para frente',
    descricao: 'O tronco volta do encosto e projeta-se para frente; agora o cinto entra em carga.',
    posicaoVeiculo: z(1.5),
    rotacaoVeiculoRad: nulo,
    velocidadeVeiculo: 0.34,
    deslocamentoRelativoOcupante: z(0.12),
    inclinacaoTroncoRad: 0.18,
    inclinacaoLateralTroncoRad: 0,
    inclinacaoCabecaRad: 0.3,
    expansaoAirbag: 0,
  },
  {
    tempo: 1,
    fase: 'Posição final',
    descricao: 'Posição final para análise. Não representa medida biomecânica.',
    posicaoVeiculo: z(1.72),
    rotacaoVeiculoRad: nulo,
    velocidadeVeiculo: 0,
    deslocamentoRelativoOcupante: z(0.04),
    inclinacaoTroncoRad: 0.08,
    inclinacaoLateralTroncoRad: 0,
    inclinacaoCabecaRad: 0.12,
    expansaoAirbag: 0,
  },
]

/**
 * Capotamento com uma volta: sobre as rodas → sobre a lateral → invertido.
 *
 * A rolagem é em torno do eixo longitudinal (+Z) e NEGATIVA, o que tomba o
 * veículo sobre o lado do motorista (+X; ver `ROLAGEM_SOBRE_O_MOTORISTA_RAD`). A cena sobe o veículo conforme ele gira,
 * senão ele atravessaria o solo — e é a mesma altura de apoio que
 * `postoDoVeiculo` usa nas configurações estáticas da extração.
 *
 * As duas paradas intermediárias são deliberadas: é nelas que o aluno pausa para
 * ver o veículo sobre a lateral e depois invertido, que são as duas
 * configurações em que a equipe pode encontrar a cena.
 *
 * O caminho deriva para +X — o lado sobre o qual o veículo tomba — e FICA lá. Até
 * aqui ele saía da origem, ia a −0,6 m em X e VOLTAVA à origem no último quadro,
 * só para honrar um "termina na origem" que nunca valeu para lateral nem para
 * traseira. Eram 60 cm de deslize lateral — o maior trecho de toda a sequência —
 * com o veículo já a 10% da velocidade, e era de onde saía o salto de +5,1 km/h
 * em t=0,8. Um veículo que capota deriva no sentido em que rola e para ali.
 */
const CAPOTAMENTO: readonly Chave[] = [
  {
    tempo: 0,
    fase: 'Sobre as rodas',
    descricao: 'Veículo em movimento, ainda sobre as rodas.',
    posicaoVeiculo: vetor(-0.6, 0, -1.4),
    rotacaoVeiculoRad: nulo,
    velocidadeVeiculo: 1,
    deslocamentoRelativoOcupante: nulo,
    inclinacaoTroncoRad: 0,
    inclinacaoLateralTroncoRad: 0,
    inclinacaoCabecaRad: 0.05,
    expansaoAirbag: 0,
  },
  {
    tempo: 0.1,
    fase: 'Início da rolagem',
    descricao: 'O veículo perde apoio de um lado e começa a girar em torno do eixo longitudinal.',
    posicaoVeiculo: vetor(-0.5, 0, -0.9),
    rotacaoVeiculoRad: z(-0.5),
    velocidadeVeiculo: 0.85,
    deslocamentoRelativoOcupante: vetor(0.04, 0, 0),
    inclinacaoTroncoRad: 0,
    inclinacaoLateralTroncoRad: -0.1,
    inclinacaoCabecaRad: 0.06,
    expansaoAirbag: 0.35,
  },
  {
    tempo: 0.22,
    fase: 'Sobre a lateral',
    descricao:
      'Primeira configuração em que a equipe pode encontrar o veículo: apoiado sobre a lateral, a 90°.',
    posicaoVeiculo: vetor(-0.3, 0, -0.5),
    rotacaoVeiculoRad: z(-Math.PI / 2),
    velocidadeVeiculo: 0.62,
    deslocamentoRelativoOcupante: vetor(0.09, 0.03, 0),
    inclinacaoTroncoRad: 0.04,
    inclinacaoLateralTroncoRad: -0.22,
    inclinacaoCabecaRad: 0.1,
    expansaoAirbag: 1,
  },
  {
    tempo: 0.38,
    fase: 'Passagem pelo teto',
    descricao: 'O veículo passa apoiado na quina do teto; o ocupante é retido pelo cinto.',
    posicaoVeiculo: vetor(-0.12, 0, -0.22),
    rotacaoVeiculoRad: z(-2.2),
    velocidadeVeiculo: 0.38,
    deslocamentoRelativoOcupante: vetor(0.1, 0.09, 0),
    inclinacaoTroncoRad: 0.06,
    inclinacaoLateralTroncoRad: -0.2,
    inclinacaoCabecaRad: 0.12,
    expansaoAirbag: 1,
  },
  {
    tempo: 0.5,
    fase: 'Impacto invertido',
    descricao:
      'Veículo invertido, inclinado com o cofre do motor apoiado no solo. É esta a configuração da extração no capotamento.',
    posicaoVeiculo: vetor(-0.06, 0, -0.14),
    rotacaoVeiculoRad: vetor(INCLINACAO_CAPOTADO_RAD, 0, -Math.PI),
    velocidadeVeiculo: 0.26,
    deslocamentoRelativoOcupante: vetor(0.06, 0.14, 0),
    inclinacaoTroncoRad: 0.04,
    inclinacaoLateralTroncoRad: -0.1,
    inclinacaoCabecaRad: 0.1,
    expansaoAirbag: 0.8,
  },
  {
    tempo: 0.74,
    fase: 'Deslizamento no teto',
    descricao:
      'O veículo continua o movimento deslizando sobre o teto e capô, dissipando energia cinética por atrito.',
    posicaoVeiculo: vetor(-0.02, 0, -0.05),
    rotacaoVeiculoRad: vetor(INCLINACAO_CAPOTADO_RAD, 0, -Math.PI),
    velocidadeVeiculo: 0.12,
    deslocamentoRelativoOcupante: vetor(0.05, 0.15, 0),
    inclinacaoTroncoRad: 0.03,
    inclinacaoLateralTroncoRad: -0.08,
    inclinacaoCabecaRad: 0.09,
    expansaoAirbag: 0.65,
  },
  {
    tempo: 1,
    fase: 'Acomodação',
    descricao:
      'Veículo parado e invertido, com a frente mais baixa pelo apoio do cofre do motor. Para a retirada, o teto passa a ser o piso inclinado — e é sobre ele que a vítima sem cinto fica deitada. Quem estava de cinto continua no banco, suspenso de cabeça para baixo.',
    posicaoVeiculo: nulo,
    rotacaoVeiculoRad: vetor(INCLINACAO_CAPOTADO_RAD, 0, -Math.PI),
    velocidadeVeiculo: 0,
    deslocamentoRelativoOcupante: vetor(0.04, 0.16, 0),
    inclinacaoTroncoRad: 0.02,
    inclinacaoLateralTroncoRad: -0.06,
    inclinacaoCabecaRad: 0.08,
    expansaoAirbag: 0.5,
  },
]

/**
 * Quadros-chave do tombamento: um quarto de volta, e para aí.
 *
 * Existe separado do capotamento porque a aula os separa, e porque as outras
 * abas já tratam o resultado como duas coisas diferentes — a estabilização treina
 * o cenário `tombado` e o capotado em níveis distintos. Faltava mostrar COMO o
 * veículo chega a cada um.
 *
 * A diferença que o aluno precisa ver: aqui o veículo TROPEÇA — perde apoio de um
 * lado e gira em torno do eixo longitudinal até a lateral encontrar o solo — e
 * permanece a 90°. No capotamento a rotação continua e ele termina invertido.
 * Para a vítima isso muda tudo: tombada, ela fica pendurada no cinto contra a
 * lateral que virou piso; invertida, ela fica sobre o teto.
 */
const TOMBAMENTO: readonly Chave[] = [
  {
    tempo: 0,
    fase: 'Sobre as rodas',
    descricao: 'Veículo em movimento, ainda sobre as rodas.',
    posicaoVeiculo: z(-1.5),
    rotacaoVeiculoRad: nulo,
    velocidadeVeiculo: 1,
    deslocamentoRelativoOcupante: nulo,
    inclinacaoTroncoRad: 0,
    inclinacaoLateralTroncoRad: 0,
    inclinacaoCabecaRad: 0.05,
    expansaoAirbag: 0,
  },
  {
    tempo: 0.1,
    fase: 'Tropeço',
    descricao:
      'A roda encontra resistência lateral — meio-fio, valeta, solo mole — e o veículo começa a girar sobre o eixo longitudinal.',
    posicaoVeiculo: z(-1.02),
    rotacaoVeiculoRad: z(-0.22),
    velocidadeVeiculo: 0.82,
    deslocamentoRelativoOcupante: vetor(0.03, 0, 0),
    inclinacaoTroncoRad: 0,
    inclinacaoLateralTroncoRad: -0.08,
    inclinacaoCabecaRad: 0.06,
    expansaoAirbag: 0.3,
  },
  {
    tempo: 0.24,
    fase: 'Perda de apoio',
    descricao: 'As rodas do lado alto deixam o solo. É o cinto que mantém o ocupante no banco.',
    posicaoVeiculo: z(-0.58),
    rotacaoVeiculoRad: z(-0.85),
    velocidadeVeiculo: 0.55,
    deslocamentoRelativoOcupante: vetor(0.08, 0.02, 0),
    inclinacaoTroncoRad: 0.03,
    inclinacaoLateralTroncoRad: -0.2,
    inclinacaoCabecaRad: 0.1,
    expansaoAirbag: 1,
  },
  {
    tempo: 0.36,
    fase: 'Queda sobre a lateral',
    descricao: 'O peso passa para o lado baixo e a lateral vai ao encontro do solo.',
    posicaoVeiculo: z(-0.26),
    rotacaoVeiculoRad: z(-1.3),
    velocidadeVeiculo: 0.34,
    deslocamentoRelativoOcupante: vetor(0.12, 0.05, 0),
    inclinacaoTroncoRad: 0.05,
    inclinacaoLateralTroncoRad: -0.26,
    inclinacaoCabecaRad: 0.13,
    expansaoAirbag: 1,
  },
  {
    tempo: 0.5,
    fase: 'Contato lateral e deslizamento',
    descricao:
      'O veículo atinge o solo a 90° e continua deslizando pela inércia, iniciando a frenagem pela lataria.',
    posicaoVeiculo: z(-0.15),
    rotacaoVeiculoRad: z(-Math.PI / 2),
    velocidadeVeiculo: 0.24,
    deslocamentoRelativoOcupante: vetor(0.14, 0.07, 0),
    inclinacaoTroncoRad: 0.04,
    inclinacaoLateralTroncoRad: -0.24,
    inclinacaoCabecaRad: 0.12,
    expansaoAirbag: 0.85,
  },
  {
    tempo: 0.74,
    fase: 'Desaceleração por atrito',
    descricao:
      'Deslizamento lateral sobre a lataria com desaceleração contínua até o repouso total.',
    posicaoVeiculo: z(-0.05),
    rotacaoVeiculoRad: z(-Math.PI / 2),
    velocidadeVeiculo: 0.1,
    deslocamentoRelativoOcupante: vetor(0.14, 0.075, 0),
    inclinacaoTroncoRad: 0.035,
    inclinacaoLateralTroncoRad: -0.22,
    inclinacaoCabecaRad: 0.11,
    expansaoAirbag: 0.65,
  },
  {
    tempo: 1,
    fase: 'Acomodação',
    descricao:
      'Veículo estabilizável sobre a lateral. Com cinto, a vítima fica suspensa contra a porta que virou piso; sem cinto, ela cai sobre essa porta. É essa a configuração que o treino de estabilização chama de tombado.',
    posicaoVeiculo: nulo,
    rotacaoVeiculoRad: z(-Math.PI / 2),
    velocidadeVeiculo: 0,
    deslocamentoRelativoOcupante: vetor(0.13, 0.08, 0),
    inclinacaoTroncoRad: 0.03,
    inclinacaoLateralTroncoRad: -0.2,
    inclinacaoCabecaRad: 0.1,
    expansaoAirbag: 0.5,
  },
]

/** A partir deste instante o cinto/pretensionador entram em evidência. */
export const TEMPO_RETENCAO = 0.35
export const TEMPO_PRETENSIONADOR = 0.3

/**
 * Lateral que capota: outro veículo bate na lateral do passageiro, o veículo
 * perde o apoio dos pneus do lado oposto e rola até ficar invertido.
 *
 * A rolagem é NEGATIVA, como no tombamento/capotamento — o lado do motorista
 * (+X) é o que vai ao solo — o que exige que o impacto venha do lado OPOSTO
 * (face −X, o lado do passageiro; a força aponta para +X): um veículo capota
 * tropeçando nos próprios pneus do lado que NÃO foi atingido, não do lado
 * que foi. As primeiras fases (sem rolagem) espelham em X a autoria de
 * `LATERAL`; a partir da perda de apoio, a cauda reaproveita a mesma forma de
 * `CAPOTAMENTO`.
 */
const LATERAL_COM_CAPOTAMENTO: readonly Chave[] = [
  {
    tempo: 0,
    fase: 'Antes do impacto',
    descricao: 'Veículo parado. O impacto vem pela lateral do passageiro.',
    posicaoVeiculo: nulo,
    rotacaoVeiculoRad: nulo,
    velocidadeVeiculo: 0,
    deslocamentoRelativoOcupante: nulo,
    inclinacaoTroncoRad: 0,
    inclinacaoLateralTroncoRad: 0,
    inclinacaoCabecaRad: 0.05,
    expansaoAirbag: 0,
  },
  {
    tempo: 0.08,
    fase: 'Contato lateral',
    descricao: 'Contato na lateral do passageiro; o veículo ainda está sobre as quatro rodas.',
    posicaoVeiculo: vetor(0.04, 0, 0),
    rotacaoVeiculoRad: nulo,
    velocidadeVeiculo: 0.3,
    deslocamentoRelativoOcupante: nulo,
    inclinacaoTroncoRad: 0,
    inclinacaoLateralTroncoRad: 0.02,
    inclinacaoCabecaRad: 0.05,
    expansaoAirbag: 0,
  },
  {
    tempo: 0.16,
    fase: 'Aceleração lateral',
    descricao:
      'O veículo é empurrado para o lado oposto ao impacto; o ocupante fica para trás, indo ao encontro da lateral que entra.',
    posicaoVeiculo: vetor(0.22, 0, 0),
    rotacaoVeiculoRad: nulo,
    velocidadeVeiculo: 0.68,
    deslocamentoRelativoOcupante: vetor(-0.1, 0, 0),
    inclinacaoTroncoRad: 0,
    inclinacaoLateralTroncoRad: 0.14,
    inclinacaoCabecaRad: 0.06,
    expansaoAirbag: 0.5,
  },
  {
    tempo: 0.22,
    fase: 'Perda de apoio',
    descricao:
      'As rodas do lado oposto ao impacto perdem contato com o solo; o veículo começa a girar em torno do eixo longitudinal.',
    posicaoVeiculo: vetor(0.35, 0, 0),
    rotacaoVeiculoRad: z(-0.3),
    velocidadeVeiculo: 0.58,
    deslocamentoRelativoOcupante: vetor(-0.12, 0.01, 0),
    inclinacaoTroncoRad: 0,
    inclinacaoLateralTroncoRad: 0.05,
    inclinacaoCabecaRad: 0.08,
    expansaoAirbag: 1,
  },
  {
    tempo: 0.32,
    fase: 'Sobre a lateral',
    descricao: 'Veículo apoiado sobre a lateral do motorista, a 90°.',
    posicaoVeiculo: vetor(0.45, 0, 0),
    rotacaoVeiculoRad: z(-Math.PI / 2),
    velocidadeVeiculo: 0.4,
    deslocamentoRelativoOcupante: vetor(-0.02, 0.05, 0),
    inclinacaoTroncoRad: 0.02,
    inclinacaoLateralTroncoRad: -0.15,
    inclinacaoCabecaRad: 0.1,
    expansaoAirbag: 1,
  },
  {
    tempo: 0.44,
    fase: 'Passagem pelo teto',
    descricao: 'O veículo passa apoiado na quina do teto.',
    posicaoVeiculo: vetor(0.52, 0, 0),
    rotacaoVeiculoRad: z(-2.2),
    velocidadeVeiculo: 0.22,
    deslocamentoRelativoOcupante: vetor(0.08, 0.09, 0),
    inclinacaoTroncoRad: 0.05,
    inclinacaoLateralTroncoRad: -0.12,
    inclinacaoCabecaRad: 0.12,
    expansaoAirbag: 1,
  },
  {
    tempo: 0.55,
    fase: 'Invertido',
    descricao:
      'Veículo invertido, inclinado com o cofre do motor apoiado no solo — a configuração da extração.',
    posicaoVeiculo: vetor(0.55, 0, 0),
    rotacaoVeiculoRad: vetor(INCLINACAO_CAPOTADO_RAD, 0, -Math.PI),
    velocidadeVeiculo: 0.08,
    deslocamentoRelativoOcupante: vetor(0.1, 0.14, 0),
    inclinacaoTroncoRad: 0.03,
    inclinacaoLateralTroncoRad: -0.08,
    inclinacaoCabecaRad: 0.1,
    expansaoAirbag: 0.75,
  },
  {
    tempo: 1,
    fase: 'Acomodação',
    descricao: 'Veículo parado e invertido.',
    posicaoVeiculo: vetor(0.55, 0, 0),
    rotacaoVeiculoRad: vetor(INCLINACAO_CAPOTADO_RAD, 0, -Math.PI),
    velocidadeVeiculo: 0,
    deslocamentoRelativoOcupante: vetor(0.06, 0.16, 0),
    inclinacaoTroncoRad: 0.02,
    inclinacaoLateralTroncoRad: -0.05,
    inclinacaoCabecaRad: 0.08,
    expansaoAirbag: 0.45,
  },
]

/**
 * Frontal seguida de traseira: engavetamento numa única linha do tempo.
 *
 * A primeira metade é a mesma lição da `FRONTAL` — desaceleração, corpo
 * continua para frente, cinto e airbag entram em carga. Antes de a régua
 * acabar, um segundo veículo bate atrás: o carro, já quase parado, é
 * empurrado de novo para frente, e o ocupante repete a lição da `TRASEIRA` —
 * fica para trás, a cabeça atrasa em relação ao tronco. Nenhum airbag novo
 * dispara no segundo impacto: é a mesma lição de sempre, o protagonista aqui
 * também é o apoio de cabeça.
 */
const FRONTAL_COM_TRASEIRA: readonly Chave[] = [
  {
    tempo: 0,
    fase: 'Movimento',
    descricao: 'Veículo em movimento, aproximando-se do que vai atingir de frente.',
    posicaoVeiculo: z(-2.4),
    rotacaoVeiculoRad: nulo,
    velocidadeVeiculo: 1,
    deslocamentoRelativoOcupante: nulo,
    inclinacaoTroncoRad: 0,
    inclinacaoLateralTroncoRad: 0,
    inclinacaoCabecaRad: 0.05,
    expansaoAirbag: 0,
  },
  {
    tempo: 0.12,
    fase: 'Contato dianteiro',
    descricao: 'Início do contato e da desaceleração da estrutura dianteira.',
    posicaoVeiculo: z(-1.3),
    rotacaoVeiculoRad: nulo,
    velocidadeVeiculo: 0.95,
    deslocamentoRelativoOcupante: nulo,
    inclinacaoTroncoRad: 0,
    inclinacaoLateralTroncoRad: 0,
    inclinacaoCabecaRad: 0.05,
    expansaoAirbag: 0,
  },
  {
    tempo: 0.2,
    fase: 'Desaceleração',
    descricao: 'O veículo desacelera fortemente; a zona de absorção dianteira trabalha.',
    posicaoVeiculo: z(-0.55),
    rotacaoVeiculoRad: nulo,
    velocidadeVeiculo: 0.5,
    deslocamentoRelativoOcupante: z(0.14),
    inclinacaoTroncoRad: 0.18,
    inclinacaoLateralTroncoRad: 0,
    inclinacaoCabecaRad: 0.24,
    expansaoAirbag: 0.4,
  },
  {
    tempo: 0.3,
    fase: 'Retenção',
    descricao:
      'Pico do primeiro deslocamento: cinto, pretensionador e airbag em evidência, freando o ocupante contra a estrutura dianteira que já parou.',
    posicaoVeiculo: z(-0.12),
    rotacaoVeiculoRad: nulo,
    velocidadeVeiculo: 0.12,
    deslocamentoRelativoOcupante: z(0.3),
    inclinacaoTroncoRad: 0.4,
    inclinacaoLateralTroncoRad: 0,
    inclinacaoCabecaRad: 0.55,
    expansaoAirbag: 0.9,
  },
  {
    tempo: 0.4,
    fase: 'Repouso momentâneo',
    descricao: 'O veículo praticamente para; o ocupante retorna parcialmente contra o banco.',
    posicaoVeiculo: z(-0.02),
    rotacaoVeiculoRad: nulo,
    velocidadeVeiculo: 0,
    deslocamentoRelativoOcupante: z(0.14),
    inclinacaoTroncoRad: 0.15,
    inclinacaoLateralTroncoRad: 0,
    inclinacaoCabecaRad: 0.2,
    expansaoAirbag: 0.4,
  },
  {
    tempo: 0.52,
    fase: 'Contato traseiro',
    descricao: 'Com o veículo já parado, um segundo veículo bate na traseira.',
    posicaoVeiculo: nulo,
    rotacaoVeiculoRad: nulo,
    velocidadeVeiculo: 0,
    deslocamentoRelativoOcupante: z(0.08),
    inclinacaoTroncoRad: 0.05,
    inclinacaoLateralTroncoRad: 0,
    inclinacaoCabecaRad: 0.08,
    expansaoAirbag: 0.15,
  },
  {
    tempo: 0.62,
    fase: 'Aceleração pelo impacto traseiro',
    descricao: 'O segundo impacto acelera o veículo para frente outra vez; o ocupante fica para trás.',
    posicaoVeiculo: z(0.18),
    rotacaoVeiculoRad: nulo,
    velocidadeVeiculo: 0.5,
    deslocamentoRelativoOcupante: z(-0.08),
    inclinacaoTroncoRad: -0.06,
    inclinacaoLateralTroncoRad: 0,
    inclinacaoCabecaRad: -0.2,
    expansaoAirbag: 0.05,
  },
  {
    tempo: 0.74,
    fase: 'Defasagem cabeça-tronco',
    descricao:
      'O encosto acelera o tronco, mas a cabeça só é acelerada quando encontra o apoio: é a mesma defasagem da colisão traseira isolada.',
    posicaoVeiculo: z(0.5),
    rotacaoVeiculoRad: nulo,
    velocidadeVeiculo: 0.55,
    deslocamentoRelativoOcupante: z(-0.14),
    inclinacaoTroncoRad: -0.11,
    inclinacaoLateralTroncoRad: 0,
    inclinacaoCabecaRad: -0.5,
    expansaoAirbag: 0,
  },
  {
    tempo: 0.88,
    fase: 'Rebote para frente',
    descricao: 'O tronco volta do encosto e projeta-se para frente; o cinto volta a entrar em carga.',
    posicaoVeiculo: z(0.85),
    rotacaoVeiculoRad: nulo,
    velocidadeVeiculo: 0.25,
    deslocamentoRelativoOcupante: z(0.1),
    inclinacaoTroncoRad: 0.16,
    inclinacaoLateralTroncoRad: 0,
    inclinacaoCabecaRad: 0.26,
    expansaoAirbag: 0,
  },
  {
    tempo: 1,
    fase: 'Posição final',
    descricao: 'Posição final para análise. Não representa medida biomecânica.',
    posicaoVeiculo: z(1.05),
    rotacaoVeiculoRad: nulo,
    velocidadeVeiculo: 0,
    deslocamentoRelativoOcupante: z(0.04),
    inclinacaoTroncoRad: 0.08,
    inclinacaoLateralTroncoRad: 0,
    inclinacaoCabecaRad: 0.12,
    expansaoAirbag: 0,
  },
]

/**
 * Abalroamento: raspagem lateral entre dois veículos em movimento.
 *
 * O outro veículo, na faixa ao lado do motorista, fecha sobre ele e as duas
 * laterais se arrastam. Quase toda a velocidade é TANGENCIAL — ela risca e
 * amassa pouco — e só a pequena componente que fecha uma lateral sobre a
 * outra vira impacto. O veículo da maquete é empurrado para −X (para longe do
 * contato, que é na face +X, a do motorista), perde um pouco de velocidade e
 * segue; o ocupante vai levemente ao encontro da porta. É o contraste com a
 * lateral "em T": mesma região, energia muito menor.
 */
const ABALROAMENTO: readonly Chave[] = [
  {
    tempo: 0,
    fase: 'Lado a lado',
    descricao: 'Os dois veículos seguem em faixas vizinhas; o outro começa a fechar sobre o lado do motorista.',
    posicaoVeiculo: z(-3),
    rotacaoVeiculoRad: nulo,
    velocidadeVeiculo: 1,
    deslocamentoRelativoOcupante: nulo,
    inclinacaoTroncoRad: 0,
    inclinacaoLateralTroncoRad: 0,
    inclinacaoCabecaRad: 0.05,
    expansaoAirbag: 0,
  },
  {
    tempo: 0.24,
    fase: 'Contato lateral',
    descricao: 'As laterais se tocam num ângulo pequeno: começa a raspagem.',
    posicaoVeiculo: z(-1.2),
    rotacaoVeiculoRad: nulo,
    velocidadeVeiculo: 0.97,
    deslocamentoRelativoOcupante: nulo,
    inclinacaoTroncoRad: 0,
    inclinacaoLateralTroncoRad: 0,
    inclinacaoCabecaRad: 0.05,
    expansaoAirbag: 0,
  },
  {
    tempo: 0.42,
    fase: 'Raspagem',
    descricao: 'As chapas se arrastam: riscos longos e amassado raso ao longo das portas. O veículo é empurrado para o lado.',
    posicaoVeiculo: vetor(-0.1, 0, -0.1),
    rotacaoVeiculoRad: nulo,
    velocidadeVeiculo: 0.9,
    deslocamentoRelativoOcupante: vetor(0.05, 0, 0),
    inclinacaoTroncoRad: 0,
    inclinacaoLateralTroncoRad: -0.08,
    inclinacaoCabecaRad: 0.06,
    expansaoAirbag: 0,
  },
  {
    tempo: 0.58,
    fase: 'Separação',
    descricao: 'Os veículos se separam; cada um segue com uma pequena guinada.',
    posicaoVeiculo: vetor(-0.24, 0, 0.8),
    rotacaoVeiculoRad: nulo,
    velocidadeVeiculo: 0.72,
    deslocamentoRelativoOcupante: vetor(0.04, 0, 0),
    inclinacaoTroncoRad: 0.02,
    inclinacaoLateralTroncoRad: -0.06,
    inclinacaoCabecaRad: 0.05,
    expansaoAirbag: 0,
  },
  {
    tempo: 0.8,
    fase: 'Frenagem',
    descricao: 'O condutor freia; o ocupante volta à posição sentada.',
    posicaoVeiculo: vetor(-0.34, 0, 1.6),
    rotacaoVeiculoRad: nulo,
    velocidadeVeiculo: 0.3,
    deslocamentoRelativoOcupante: vetor(0.02, 0, 0.03),
    inclinacaoTroncoRad: 0.05,
    inclinacaoLateralTroncoRad: -0.03,
    inclinacaoCabecaRad: 0.06,
    expansaoAirbag: 0,
  },
  {
    tempo: 1,
    fase: 'Posição final',
    descricao: 'Veículo parado ao lado da faixa, com a lateral do motorista riscada e amassada.',
    posicaoVeiculo: vetor(-0.36, 0, 1.8),
    rotacaoVeiculoRad: nulo,
    velocidadeVeiculo: 0,
    deslocamentoRelativoOcupante: nulo,
    inclinacaoTroncoRad: 0,
    inclinacaoLateralTroncoRad: 0,
    inclinacaoCabecaRad: 0.05,
    expansaoAirbag: 0,
  },
]

/**
 * Lateral contra objeto fixo: o veículo derrapa de lado e o lado do motorista
 * encontra um poste, uma árvore ou um muro (o "side pole" dos ensaios).
 *
 * Ao contrário da lateral "em T", aqui é o PRÓPRIO veículo que se move: ele
 * vai para +X, o objeto para a face +X e o ocupante continua para +X até
 * encontrar a porta que está entrando. Poste concentra a intrusão numa faixa
 * estreita da porta — é o caso em que a coluna B e o banco do motorista mais
 * sofrem.
 */
const LATERAL_CONTRA_OBJETO: readonly Chave[] = [
  {
    tempo: 0,
    fase: 'Derrapagem lateral',
    descricao: 'O veículo perdeu a aderência e desliza de lado, com o lado do motorista à frente.',
    posicaoVeiculo: vetor(-2, 0, 0),
    rotacaoVeiculoRad: nulo,
    velocidadeVeiculo: 1,
    deslocamentoRelativoOcupante: nulo,
    inclinacaoTroncoRad: 0,
    inclinacaoLateralTroncoRad: 0,
    inclinacaoCabecaRad: 0.05,
    expansaoAirbag: 0,
  },
  {
    tempo: 0.18,
    fase: 'Contato',
    descricao: 'A lateral do motorista encontra o objeto fixo. Não há zona de absorção longitudinal a favor.',
    posicaoVeiculo: vetor(-0.45, 0, 0),
    rotacaoVeiculoRad: nulo,
    velocidadeVeiculo: 0.95,
    deslocamentoRelativoOcupante: nulo,
    inclinacaoTroncoRad: 0,
    inclinacaoLateralTroncoRad: -0.02,
    inclinacaoCabecaRad: 0.05,
    expansaoAirbag: 0,
  },
  {
    tempo: 0.28,
    fase: 'Intrusão',
    descricao: 'A porta e a coluna B entram; o ocupante continua o deslizamento e vai ao encontro delas.',
    posicaoVeiculo: vetor(-0.12, 0, 0),
    rotacaoVeiculoRad: nulo,
    velocidadeVeiculo: 0.45,
    deslocamentoRelativoOcupante: vetor(0.1, 0, 0),
    inclinacaoTroncoRad: 0,
    inclinacaoLateralTroncoRad: -0.16,
    inclinacaoCabecaRad: 0.06,
    expansaoAirbag: 0.4,
  },
  {
    tempo: 0.38,
    fase: 'SRS lateral',
    descricao: 'Airbag lateral e de cortina do lado do motorista, quando o perfil os declara.',
    posicaoVeiculo: vetor(-0.03, 0, 0),
    rotacaoVeiculoRad: nulo,
    velocidadeVeiculo: 0.12,
    deslocamentoRelativoOcupante: vetor(0.14, 0, 0),
    inclinacaoTroncoRad: 0,
    inclinacaoLateralTroncoRad: -0.24,
    inclinacaoCabecaRad: 0.08,
    expansaoAirbag: 1,
  },
  {
    tempo: 0.5,
    fase: 'Parada',
    descricao: 'O veículo para encostado no objeto.',
    posicaoVeiculo: nulo,
    rotacaoVeiculoRad: nulo,
    velocidadeVeiculo: 0,
    deslocamentoRelativoOcupante: vetor(0.12, 0, 0),
    inclinacaoTroncoRad: 0,
    inclinacaoLateralTroncoRad: -0.2,
    inclinacaoCabecaRad: 0.06,
    expansaoAirbag: 1,
  },
  {
    tempo: 0.72,
    fase: 'Rebote',
    descricao: 'O ocupante retorna parcialmente ao assento; a intrusão permanece.',
    posicaoVeiculo: nulo,
    rotacaoVeiculoRad: nulo,
    velocidadeVeiculo: 0,
    deslocamentoRelativoOcupante: vetor(0.06, 0, 0),
    inclinacaoTroncoRad: 0,
    inclinacaoLateralTroncoRad: -0.1,
    inclinacaoCabecaRad: 0.05,
    expansaoAirbag: 0.7,
  },
  {
    tempo: 1,
    fase: 'Posição final',
    descricao: 'Veículo parado contra o objeto, com a lateral do motorista deformada.',
    posicaoVeiculo: nulo,
    rotacaoVeiculoRad: nulo,
    velocidadeVeiculo: 0,
    deslocamentoRelativoOcupante: vetor(0.03, 0, 0),
    inclinacaoTroncoRad: 0,
    inclinacaoLateralTroncoRad: -0.05,
    inclinacaoCabecaRad: 0.05,
    expansaoAirbag: 0.5,
  },
]

export const COLISOES: Record<TipoColisao, DefinicaoColisao> = {
  frontal: {
    id: 'frontal',
    nome: 'Colisão frontal',
    descricao:
      'O veículo desacelera e o ocupante continua o deslocamento para frente. A zona de absorção dianteira e o cinto são os protagonistas.',
    direcaoImpacto: z(-1),
    srsAcionados: ['airbagMotorista', 'airbagPassageiro'],
    acionaPretensionador: true,
    tempoRetencao: TEMPO_RETENCAO,
    comRolagem: false,
    velocidadeImpactoKmh: 50,
    velocidadeRolagemGrausPorS: 0,
    quadros: FRONTAL,
  },
  lateral: {
    id: 'lateral',
    nome: 'Colisão lateral',
    descricao:
      'Não há zona de absorção longitudinal a favor: entre o impacto e o ocupante há apenas a porta e a coluna. O tronco vai ao encontro da estrutura, não se afasta dela.',
    // a força empurra para −X: o contato é na face +X, a do motorista
    direcaoImpacto: vetor(-1, 0, 0),
    srsAcionados: [
      'airbagLateralEsquerdo',
      'airbagCortinaEsquerdo',
      'airbagBancoMotorista',
      'airbagColunaMotorista',
    ],
    acionaPretensionador: true,
    tempoRetencao: TEMPO_RETENCAO,
    comRolagem: false,
    velocidadeImpactoKmh: 50,
    velocidadeRolagemGrausPorS: 0,
    quadros: LATERAL,
  },
  traseira: {
    id: 'traseira',
    nome: 'Colisão traseira',
    descricao:
      'O veículo é acelerado para frente e o ocupante fica para trás. A cabeça atrasa em relação ao tronco, e é o apoio de cabeça — não o airbag — que limita essa defasagem.',
    direcaoImpacto: z(1),
    srsAcionados: [],
    acionaPretensionador: false,
    tempoRetencao: TEMPO_RETENCAO,
    comRolagem: false,
    velocidadeImpactoKmh: 30,
    velocidadeRolagemGrausPorS: 0,
    quadros: TRASEIRA,
  },
  tombamento: {
    id: 'tombamento',
    nome: 'Tombamento',
    descricao:
      'Um quarto de volta em torno do eixo longitudinal: o veículo tropeça e para sobre a lateral. Não é capotamento — a rotação para aos 90°, e a vítima fica suspensa pelo cinto contra a porta que virou piso.',
    // não há objeto atingido: o que inicia a rotação é resistência lateral no solo
    direcaoImpacto: null,
    // só a cortina do lado que vai ao solo, pelo mesmo critério da lateral
    srsAcionados: ['airbagCortinaEsquerdo'],
    acionaPretensionador: true,
    // o cinto passa a segurar o ocupante assim que a rolagem começa
    tempoRetencao: 0.12,
    comRolagem: true,
    velocidadeImpactoKmh: 40,
    // um quarto de volta é mais lento que a volta inteira do capotamento.
    // 240°/s, e não 90: capotamentos reais giram entre 250 e 400°/s, e a taxa
    // antiga obrigava o caminho desenhado a discordar da velocidade declarada —
    // 40 km/h percorrem 11 m em 1 s, e a cena mostrava 1,5 m
    velocidadeRolagemGrausPorS: 240,
    quadros: TOMBAMENTO,
  },
  capotamento: {
    id: 'capotamento',
    nome: 'Capotamento',
    descricao:
      'Uma volta em torno do eixo longitudinal: sobre as rodas, sobre a lateral, invertido. Pausar em cada parada mostra as configurações em que a equipe pode encontrar a cena.',
    direcaoImpacto: null,
    srsAcionados: ['airbagCortinaEsquerdo', 'airbagCortinaDireito'],
    acionaPretensionador: true,
    // o cinto passa a segurar o ocupante assim que a rolagem começa
    tempoRetencao: 0.12,
    comRolagem: true,
    velocidadeImpactoKmh: 60,
    // 300°/s pela mesma razão do tombamento: é a faixa real, e é o que permite
    // ao caminho desenhado andar à velocidade que o evento declara
    velocidadeRolagemGrausPorS: 300,
    quadros: CAPOTAMENTO,
  },
  lateralComCapotamento: {
    id: 'lateralComCapotamento',
    nome: 'Lateral com capotamento',
    descricao:
      'Outro veículo bate na lateral do passageiro; o veículo perde o apoio do lado oposto e capota até ficar invertido.',
    // não-nulo, ao contrário de tombamento/capotamento: há um veículo atingindo.
    // A força empurra para +X: o contato é na face −X, a do passageiro
    direcaoImpacto: vetor(1, 0, 0),
    srsAcionados: ['airbagLateralDireito', 'airbagCortinaDireito', 'airbagCortinaEsquerdo'],
    acionaPretensionador: true,
    // o cinto entra em carga já no impacto lateral, antes de a rolagem começar
    tempoRetencao: 0.12,
    comRolagem: true,
    velocidadeImpactoKmh: 50,
    velocidadeRolagemGrausPorS: 300,
    quadros: LATERAL_COM_CAPOTAMENTO,
  },
  frontalComTraseira: {
    id: 'frontalComTraseira',
    nome: 'Frontal seguida de traseira',
    descricao:
      'Engavetamento: o veículo bate de frente e, antes de terminar de parar, é atingido por trás por um segundo veículo.',
    direcaoImpacto: z(-1),
    srsAcionados: ['airbagMotorista', 'airbagPassageiro'],
    acionaPretensionador: true,
    tempoRetencao: 0.15,
    comRolagem: false,
    velocidadeImpactoKmh: 40,
    velocidadeRolagemGrausPorS: 0,
    quadros: FRONTAL_COM_TRASEIRA,
    impactoSecundario: {
      nome: 'Segundo contato — colisão traseira',
      direcaoImpacto: z(1),
      alvo: 'outroVeiculo',
      srsAcionados: [],
      acionaPretensionador: false,
    },
  },
  abalroamento: {
    id: 'abalroamento',
    nome: 'Abalroamento (raspagem lateral)',
    descricao:
      'Dois veículos em movimento raspam as laterais. Quase toda a velocidade é tangencial: risca e amassa pouco. Só o que uma lateral fecha sobre a outra vira impacto — compare com a lateral em T.',
    // a força empurra para −X: o contato é na face +X, a do motorista
    direcaoImpacto: vetor(-1, 0, 0),
    // energia lateral baixa: nenhum dispositivo é acionado nesta sequência
    srsAcionados: [],
    acionaPretensionador: false,
    tempoRetencao: TEMPO_RETENCAO,
    comRolagem: false,
    velocidadeImpactoKmh: 60,
    velocidadeRolagemGrausPorS: 0,
    quadros: ABALROAMENTO,
  },
  lateralContraObjeto: {
    id: 'lateralContraObjeto',
    nome: 'Lateral contra poste/objeto fixo',
    descricao:
      'O veículo derrapa de lado e o lado do motorista encontra um poste, árvore ou muro. É o próprio veículo que se move contra o objeto; o poste concentra a intrusão na porta e na coluna B.',
    // a força do objeto empurra para −X: o contato é na face +X, a do motorista
    direcaoImpacto: vetor(-1, 0, 0),
    srsAcionados: [
      'airbagLateralEsquerdo',
      'airbagCortinaEsquerdo',
      'airbagBancoMotorista',
      'airbagColunaMotorista',
    ],
    acionaPretensionador: true,
    tempoRetencao: TEMPO_RETENCAO,
    comRolagem: false,
    velocidadeImpactoKmh: 30,
    velocidadeRolagemGrausPorS: 0,
    quadros: LATERAL_CONTRA_OBJETO,
  },
}

export const colisao = (tipo: TipoColisao): DefinicaoColisao => COLISOES[tipo]

/**
 * Velocidades de impacto oferecidas ao aluno, em km/h.
 *
 * A velocidade deixou de ser constante do tipo de colisão e passou a ser
 * escolha: é o controle que mostra, num gesto, que a energia cresce com o
 * QUADRADO dela. A lista mora aqui, e não na UI, pela mesma razão que todo o
 * resto — número de aula é dado do domínio.
 */
export const VELOCIDADES_IMPACTO: readonly number[] = [20, 30, 40, 50, 60, 80, 100]

/** A velocidade em vigor: a escolhida pelo aluno, ou a declarada pelo tipo. */
export const velocidadeDoEvento = (tipo: TipoColisao, velocidadeKmh?: number): number =>
  velocidadeKmh != null && velocidadeKmh > 0 ? velocidadeKmh : colisao(tipo).velocidadeImpactoKmh

/** Caminho percorrido pelo veículo na sequência, em metros — a polilinha autoral. */
export function distanciaPercorrida(tipo: TipoColisao): number {
  return tabelaDoCaminho(tipo).total
}

/**
 * Integral de `velocidadeVeiculo` na escala normalizada, entre 0 e `ate`.
 *
 * É o que mede QUANTO do caminho já foi percorrido: com a posição derivada da
 * velocidade, esta integral é a única coisa que decide onde o veículo está.
 */
export function integralVelocidadeAte(tipo: TipoColisao, ate: number): number {
  const quadros = colisao(tipo).quadros
  const limite = limitar(ate, 0, 1)
  let soma = 0
  for (let i = 0; i < quadros.length - 1; i += 1) {
    const a = quadros[i]!
    const b = quadros[i + 1]!
    if (a.tempo >= limite) break
    const fim = Math.min(b.tempo, limite)
    const largura = b.tempo - a.tempo
    const fracao = largura <= 0 ? 0 : (fim - a.tempo) / largura
    const vFim = a.velocidadeVeiculo + (b.velocidadeVeiculo - a.velocidadeVeiculo) * fracao
    soma += ((a.velocidadeVeiculo + vFim) / 2) * (fim - a.tempo)
    if (fim >= limite) break
  }
  return soma
}

/** Rolagem total do veículo na sequência, em radianos. */
export function rolagemTotalRad(tipo: TipoColisao): number {
  const quadros = colisao(tipo).quadros
  let soma = 0
  for (let i = 0; i < quadros.length - 1; i += 1) {
    soma += Math.abs(quadros[i + 1]!.rotacaoVeiculoRad.z - quadros[i]!.rotacaoVeiculoRad.z)
  }
  return soma
}

/** Tempo que a translação desenhada exige, à velocidade em vigor. */
function duracaoPelaTranslacao(tipo: TipoColisao, velocidadeKmh?: number): number {
  const velocidadeMs = velocidadeDoEvento(tipo, velocidadeKmh) / 3.6
  const integral = integralVelocidadeAte(tipo, 1)
  if (velocidadeMs < 1e-9 || integral < 1e-9) return 0
  return distanciaPercorrida(tipo) / (velocidadeMs * integral)
}

/** Instante em que a rolagem termina — depois dele o veículo só acomoda. */
export function fimDaRolagem(tipo: TipoColisao): number {
  const quadros = colisao(tipo).quadros
  const total = quadros[quadros.length - 1]!.rotacaoVeiculoRad.z
  for (const quadro of quadros) {
    if (Math.abs(quadro.rotacaoVeiculoRad.z - total) < 1e-9) return quadro.tempo
  }
  return 1
}

/**
 * Instante em que a rolagem começa: o último quadro que ainda está no ângulo
 * inicial, antes dele começar a mudar.
 *
 * Existe porque nem toda colisão com rolagem começa a rolar em t=0 — a
 * "lateral que capota" tem uma fase de impacto antes de perder o apoio. Nos
 * dois tipos que já existiam (tombamento/capotamento), a rolagem sempre
 * começou no primeiro quadro, então isto devolve exatamente 0 para eles.
 */
export function inicioDaRolagem(tipo: TipoColisao): number {
  const quadros = colisao(tipo).quadros
  const inicial = quadros[0]!.rotacaoVeiculoRad.z
  let ultimo = quadros[0]!.tempo
  for (const quadro of quadros) {
    if (Math.abs(quadro.rotacaoVeiculoRad.z - inicial) < 1e-9) ultimo = quadro.tempo
    else break
  }
  return ultimo
}

/**
 * Tempo que a sequência exige para que a rolagem ocorra à taxa declarada.
 *
 * Dividido pela JANELA da régua em que a rolagem de fato acontece — de
 * `inicioDaRolagem` a `fimDaRolagem` —, e não pela régua inteira nem só pelo
 * fim. Antes, o ângulo total sobre a taxa era atribuído aos 0..1 completos,
 * então a rotação desenhada corria mais rápido que a taxa declarada —
 * meia-volta em 80% da régua dava 375°/s onde o tipo declarava 300. Usar só o
 * fim bastava enquanto toda rolagem começava em t=0; a "lateral que capota"
 * tem uma fase de impacto antes disso, que não é tempo de rolagem.
 *
 * O resto da régua é a ACOMODAÇÃO, e ela precisa de tempo real: é nela que o
 * corpo sem cinto cai sobre o teto que virou piso. Sem esse tempo o boneco
 * terminava a meio caminho, suspenso no ar — a rolagem acabava e a régua acabava
 * junto, antes de a gravidade ter feito o trabalho que a aula vai mostrar.
 */
function duracaoPelaRolagem(tipo: TipoColisao): number {
  const taxa = colisao(tipo).velocidadeRolagemGrausPorS
  if (taxa < 1e-9) return 0
  const fracao = Math.max(fimDaRolagem(tipo) - inicioDaRolagem(tipo), 1e-6)
  return (rolagemTotalRad(tipo) * 180) / Math.PI / taxa / fracao
}

/**
 * Duração REAL da sequência, em segundos — não a da reprodução.
 *
 * Derivada, não declarada, e pelo movimento que MAIS tempo exige: a translação
 * desenhada à velocidade em vigor, ou a rolagem desenhada à taxa de rolagem
 * declarada. Nas três colisões quem manda é a translação; nas duas que rolam é a
 * rolagem, porque meia-volta não acontece em três décimos de segundo.
 *
 * Existe porque a reprodução é lenta de propósito (`DURACAO_BASE_S`, 4,5 s): uma
 * física integrada no tempo de reprodução daria à gravidade 4,5 s para agir e a
 * carga solta cairia dezenas de metros dentro do habitáculo.
 */
export function duracaoRealS(tipo: TipoColisao, velocidadeKmh?: number): number {
  return Math.max(duracaoPelaTranslacao(tipo, velocidadeKmh), duracaoPelaRolagem(tipo))
}

/**
 * Comprimento que o caminho precisa ter para o veículo andar na velocidade que
 * o evento declara, em metros.
 *
 * Nas colisões é exatamente `distanciaPercorrida` — a duração sai justamente
 * dessa conta, então o caminho autoral já é o coerente. Nas duas que rolam quem
 * dita a duração é a rotação, e o caminho tem de ESTICAR para acompanhar: um
 * veículo a 60 km/h não percorre um metro e meio enquanto dá meia-volta. É este
 * o preço de a velocidade declarada ser a verdade, e é o que faz o velocímetro
 * do painel marcar o mesmo que o seletor de velocidade.
 */
export function comprimentoDoCaminho(tipo: TipoColisao, velocidadeKmh?: number): number {
  const velocidadeMs = velocidadeDoEvento(tipo, velocidadeKmh) / 3.6
  return velocidadeMs * integralVelocidadeAte(tipo, 1) * duracaoRealS(tipo, velocidadeKmh)
}

/** Quanto a polilinha autoral é esticada, com o último quadro como âncora. */
function escalaDoCaminho(tipo: TipoColisao, velocidadeKmh?: number): number {
  const total = distanciaPercorrida(tipo)
  if (total < 1e-9) return 1
  return comprimentoDoCaminho(tipo, velocidadeKmh) / total
}

/**
 * Instante em que o veículo sofre a primeira variação de velocidade.
 *
 * É o contato externo — o 1º impacto da aula — e sai do perfil de velocidade que
 * a timeline já declara, em vez de ser um número novo. Antes da recalibração da
 * posição isto não era confiável; agora é a mesma grandeza que move o veículo.
 */
export function instanteDoContato(tipo: TipoColisao): number {
  const quadros = colisao(tipo).quadros
  const inicial = quadros[0]!.velocidadeVeiculo
  for (const quadro of quadros) {
    if (Math.abs(quadro.velocidadeVeiculo - inicial) > 1e-9) return quadro.tempo
  }
  return 1
}

/**
 * Todos os contatos externos do evento, em ordem.
 *
 * O primeiro é sempre `instanteDoContato`. Um segundo só existe quando o tipo
 * declara `impactoSecundario`, e o instante dele também é DERIVADO, não
 * declarado: é o último quadro em que a velocidade ainda está no seu mínimo
 * (depois do primeiro contato) antes de voltar a crescer — o veículo já
 * parado ou quase, e então empurrado de novo por trás.
 */
export function instantesDeContato(tipo: TipoColisao): readonly number[] {
  const primeiro = instanteDoContato(tipo)
  if (!colisao(tipo).impactoSecundario) return [primeiro]

  const quadros = colisao(tipo).quadros
  let minimo = Infinity
  let tempoDoMinimo = primeiro
  for (const quadro of quadros) {
    if (quadro.tempo <= primeiro) continue
    if (quadro.velocidadeVeiculo < minimo + 1e-9) {
      minimo = Math.min(minimo, quadro.velocidadeVeiculo)
      tempoDoMinimo = quadro.tempo
    } else {
      break
    }
  }
  return [primeiro, tempoDoMinimo]
}

/** Tabelas derivadas da polilinha e da rolagem, calculadas uma vez por tipo. */
interface TabelaCaminho {
  /** inclinação longitudinal, interpolada nos mesmos quadros da timeline */
  inclinacaoLongitudinal: InterpoladorMonotono
  /** comprimento acumulado até cada quadro-chave */
  acumulados: number[]
  total: number
  /** direção unitária de cada trecho; nula quando o trecho tem comprimento zero */
  direcoes: Vetor3[]
  /** último quadro: âncora em torno da qual o caminho estica */
  ancora: Vetor3
  rolagem: InterpoladorMonotono
}

const TABELAS = new Map<TipoColisao, TabelaCaminho>()

function tabelaDoCaminho(tipo: TipoColisao): TabelaCaminho {
  const existente = TABELAS.get(tipo)
  if (existente) return existente

  const quadros = colisao(tipo).quadros
  const acumulados = [0]
  const direcoes: Vetor3[] = []
  for (let i = 0; i < quadros.length - 1; i += 1) {
    const delta = subtrair(quadros[i + 1]!.posicaoVeiculo, quadros[i]!.posicaoVeiculo)
    const comprimento = Math.hypot(delta.x, delta.y, delta.z)
    acumulados.push(acumulados[i]! + comprimento)
    direcoes.push(
      comprimento < 1e-12
        ? vetor(0, 0, 0)
        : vetor(delta.x / comprimento, delta.y / comprimento, delta.z / comprimento),
    )
  }

  const tabela: TabelaCaminho = {
    acumulados,
    total: acumulados[acumulados.length - 1]!,
    direcoes,
    ancora: quadros[quadros.length - 1]!.posicaoVeiculo,
    rolagem: criarInterpoladorMonotono(
      quadros.map((q) => q.tempo),
      quadros.map((q) => q.rotacaoVeiculoRad.z),
    ),
    inclinacaoLongitudinal: criarInterpoladorMonotono(
      quadros.map((q) => q.tempo),
      quadros.map((q) => q.rotacaoVeiculoRad.x),
    ),
  }
  TABELAS.set(tipo, tabela)
  return tabela
}

/** Ponto da polilinha autoral no comprimento de arco pedido, já esticado. */
function pontoNoCaminho(tipo: TipoColisao, distancia: number, escala: number): Vetor3 {
  const tabela = tabelaDoCaminho(tipo)
  const quadros = colisao(tipo).quadros
  const alvo = limitar(distancia, 0, tabela.total)

  let bruto = quadros[quadros.length - 1]!.posicaoVeiculo
  for (let i = 0; i < tabela.direcoes.length; i += 1) {
    const inicio = tabela.acumulados[i]!
    const fim = tabela.acumulados[i + 1]!
    if (alvo <= fim || i === tabela.direcoes.length - 1) {
      const sobra = Math.max(alvo - inicio, 0)
      const direcao = tabela.direcoes[i]!
      bruto = somar(quadros[i]!.posicaoVeiculo, {
        x: direcao.x * sobra,
        y: direcao.y * sobra,
        z: direcao.z * sobra,
      })
      break
    }
  }

  if (Math.abs(escala - 1) < 1e-12) return bruto
  const relativo = subtrair(bruto, tabela.ancora)
  return somar(tabela.ancora, {
    x: relativo.x * escala,
    y: relativo.y * escala,
    z: relativo.z * escala,
  })
}

/**
 * Posição do veículo no instante — DERIVADA da velocidade, não lida do quadro.
 *
 * Aqui mora a correção de fundo. Antes, `posicaoVeiculo` era interpolado
 * linearmente no tempo entre quadros-chave: a velocidade ficava constante por
 * trecho e SALTAVA de um valor a outro em um único passo do solver. Os saltos
 * medidos eram de 13,7 km/h na frontal e 22,3 km/h na lateral — acima dos 8 km/h
 * que o solucionador usa como critério de impacto —, e o corpo dinâmico, que não
 * teleporta junto com o corpo cinemático, colhia velocidade relativa do nada. Era
 * o que fazia o boneco "machucar" mal começava a se deslocar dentro do carro.
 *
 * Com a posição saindo da integral de `velocidadeVeiculo`, o módulo da velocidade
 * é `velocidadeVeiculo(t) × velocidadeImpactoKmh` por construção: contínuo,
 * porque a curva de velocidade é contínua, e igual ao que o painel declara.
 */
export function posicaoNoCaminho(
  tipo: TipoColisao,
  tempo: number,
  velocidadeKmh?: number,
): Vetor3 {
  const tabela = tabelaDoCaminho(tipo)
  const integralTotal = integralVelocidadeAte(tipo, 1)
  const fracao = integralTotal < 1e-12 ? 0 : integralVelocidadeAte(tipo, tempo) / integralTotal
  return pontoNoCaminho(tipo, fracao * tabela.total, escalaDoCaminho(tipo, velocidadeKmh))
}

/** Rolagem do veículo no instante, em radianos, sem degraus de velocidade angular. */
export const rolagemEm = (tipo: TipoColisao, tempo: number): number =>
  tabelaDoCaminho(tipo).rolagem(limitar(tempo, 0, 1))

/** Inclinação longitudinal no instante, em radianos. */
export const inclinacaoLongitudinalEm = (tipo: TipoColisao, tempo: number): number =>
  tabelaDoCaminho(tipo).inclinacaoLongitudinal(limitar(tempo, 0, 1))

/** Taxa de inclinação longitudinal, em radianos por segundo REAL. */
export function taxaDeInclinacaoLongitudinalRadPorS(
  tipo: TipoColisao,
  tempo: number,
  velocidadeKmh?: number,
): number {
  const duracao = duracaoRealS(tipo, velocidadeKmh)
  if (duracao < 1e-12) return 0
  return tabelaDoCaminho(tipo).inclinacaoLongitudinal.derivada(limitar(tempo, 0, 1)) / duracao
}

/** Taxa de rolagem no instante, em radianos por segundo REAL. */
export function taxaDeRolagemRadPorS(
  tipo: TipoColisao,
  tempo: number,
  velocidadeKmh?: number,
): number {
  const duracao = duracaoRealS(tipo, velocidadeKmh)
  if (duracao < 1e-12) return 0
  return tabelaDoCaminho(tipo).rolagem.derivada(limitar(tempo, 0, 1)) / duracao
}

/** Instante real, em segundos, correspondente ao tempo normalizado da timeline. */
export const tempoRealS = (tipo: TipoColisao, tempo: number, velocidadeKmh?: number): number =>
  limitar(tempo, 0, 1) * duracaoRealS(tipo, velocidadeKmh)

/** Velocidade real do veículo no instante normalizado, em m/s. */
export const velocidadeRealMs = (
  tipo: TipoColisao,
  tempo: number,
  velocidadeKmh?: number,
): number =>
  (amostrarCinematica(tipo, tempo).velocidadeVeiculo * velocidadeDoEvento(tipo, velocidadeKmh)) / 3.6

const interp = (a: number, b: number, t: number): number => a + (b - a) * t
const interpV = (a: Vetor3, b: Vetor3, t: number): Vetor3 =>
  vetor(interp(a.x, b.x, t), interp(a.y, b.y, t), interp(a.z, b.z, t))

/** Amostra a timeline do tipo em `tempo` (0..1). */
export function amostrarCinematica(
  tipo: TipoColisao,
  tempo: number,
  velocidadeKmh?: number,
): QuadroCinematica {
  const { quadros, acionaPretensionador, tempoRetencao } = colisao(tipo)
  const t = limitar(tempo, 0, 1)
  let anterior = quadros[0]!
  let proximo = quadros[quadros.length - 1]!

  for (let i = 0; i < quadros.length - 1; i += 1) {
    const a = quadros[i]!
    const b = quadros[i + 1]!
    if (t >= a.tempo && t <= b.tempo) {
      anterior = a
      proximo = b
      break
    }
  }

  const intervalo = proximo.tempo - anterior.tempo
  const f = intervalo <= 0 ? 0 : (t - anterior.tempo) / intervalo

  return {
    tempo: t,
    fase: f < 0.5 ? anterior.fase : proximo.fase,
    descricao: f < 0.5 ? anterior.descricao : proximo.descricao,
    // posição e rolagem são DERIVADAS; o resto continua sendo a curva autoral
    // amostrada linearmente no tempo, que é onde a lição de fato mora
    posicaoVeiculo: posicaoNoCaminho(tipo, t, velocidadeKmh),
    rotacaoVeiculoRad: vetor(inclinacaoLongitudinalEm(tipo, t), 0, rolagemEm(tipo, t)),
    velocidadeVeiculo: interp(anterior.velocidadeVeiculo, proximo.velocidadeVeiculo, f),
    deslocamentoRelativoOcupante: interpV(
      anterior.deslocamentoRelativoOcupante,
      proximo.deslocamentoRelativoOcupante,
      f,
    ),
    inclinacaoTroncoRad: interp(anterior.inclinacaoTroncoRad, proximo.inclinacaoTroncoRad, f),
    inclinacaoLateralTroncoRad: interp(
      anterior.inclinacaoLateralTroncoRad,
      proximo.inclinacaoLateralTroncoRad,
      f,
    ),
    inclinacaoCabecaRad: interp(anterior.inclinacaoCabecaRad, proximo.inclinacaoCabecaRad, f),
    cintoAtivo: t >= tempoRetencao,
    pretensionadorAtivo: acionaPretensionador && t >= TEMPO_PRETENSIONADOR && t <= 0.72,
    expansaoAirbag: interp(anterior.expansaoAirbag, proximo.expansaoAirbag, f),
  }
}

/**
 * Altura de apoio do veículo para a rolagem e a inclinação do quadro atual.
 *
 * Girado em torno do eixo longitudinal na origem, o veículo afundaria no solo: a
 * altura de apoio é a que o traz de volta. Delega a `alturaDeApoioExata`, que
 * gira os 8 cantos da caixa do veículo pela mesma composição usada para
 * desenhá-lo e usa o mínimo — exato em qualquer ângulo intermediário. Antes
 * disso, uma interpolação por só 3 pontos de controle (0°, 90°, 180°) deixava
 * o veículo atravessar o solo em até meio metro durante a rolagem entre esses
 * ângulos, porque a curva verdadeira de apoio não é bem aproximada por uma
 * spline entre tão poucos pontos.
 */
function alturaDeApoio(perfil: PerfilVeiculo, rolagem: number, inclinacaoLongitudinal = 0): number {
  return alturaDeApoioExata(perfil, rolagem, inclinacaoLongitudinal)
}

/** Posição do veículo já com a compensação da rolagem. */
export function posicaoDoVeiculo(perfil: PerfilVeiculo, quadro: QuadroCinematica): Vetor3 {
  const rolagem = quadro.rotacaoVeiculoRad.z
  if (Math.abs(rolagem) < 1e-6) return quadro.posicaoVeiculo
  return somar(quadro.posicaoVeiculo, vetor(0, alturaDeApoio(perfil, rolagem, quadro.rotacaoVeiculoRad.x), 0))
}

/**
 * Leva um ponto do referencial do veículo para o mundo, no quadro dado.
 *
 * Os rastros têm de ser traçados no MUNDO: é exatamente a diferença entre o
 * caminho do corpo e o do veículo que a cinemática serve para mostrar. Traçá-los
 * em coordenadas do veículo os faria acompanhar o veículo e não mostrariam nada.
 */
export function paraMundo(
  perfil: PerfilVeiculo,
  quadro: QuadroCinematica,
  pontoNoVeiculo: Vetor3,
): Vetor3 {
  const inclinacao = quadro.rotacaoVeiculoRad.x
  const rolagem = quadro.rotacaoVeiculoRad.z
  const inclinado =
    Math.abs(inclinacao) < 1e-9
      ? pontoNoVeiculo
      : girarEmTorno(pontoNoVeiculo, vetor(1, 0, 0), inclinacao)
  const girado =
    Math.abs(rolagem) < 1e-9
      ? inclinado
      : girarEmTorno(inclinado, vetor(0, 0, 1), rolagem)
  return somar(girado, posicaoDoVeiculo(perfil, quadro))
}

/**
 * Inversa de `paraMundo`: traz um ponto do mundo ao referencial do veículo.
 *
 * O solucionador tinha esta mesma conta escrita à mão, e os testes de
 * capotamento precisam dela para perguntar "em que altura da cabine o corpo
 * terminou". Uma terceira cópia acabaria discordando das outras duas.
 */
export function doMundo(
  perfil: PerfilVeiculo,
  quadro: QuadroCinematica,
  pontoNoMundo: Vetor3,
): Vetor3 {
  const inclinacao = quadro.rotacaoVeiculoRad.x
  const rolagem = quadro.rotacaoVeiculoRad.z
  const relativo = subtrair(pontoNoMundo, posicaoDoVeiculo(perfil, quadro))
  const semRolagem = Math.abs(rolagem) < 1e-9
    ? relativo
    : girarEmTorno(relativo, vetor(0, 0, 1), -rolagem)
  return Math.abs(inclinacao) < 1e-9
    ? semRolagem
    : girarEmTorno(semRolagem, vetor(1, 0, 0), -inclinacao)
}

/**
 * Velocidade de MUNDO de um ponto fixo no veículo, em m/s.
 *
 * Analítica, e passa a ser a única fonte da velocidade do veículo. O
 * solucionador media isso por diferença finita em dois lugares diferentes — uma
 * para nascer o corpo com a velocidade do carro, outra para medir a velocidade
 * relativa depois —, e diferença finita sobre um caminho que dava saltos era
 * justamente o que produzia impacto falso. Aqui entram as três parcelas:
 * translação ao longo do caminho, giro do ponto em torno do eixo longitudinal, e
 * a subida do veículo sobre o apoio conforme rola.
 */
export function velocidadeNoPonto(
  perfil: PerfilVeiculo,
  tipo: TipoColisao,
  tempo: number,
  pontoNoVeiculo: Vetor3,
  velocidadeKmh?: number,
): Vetor3 {
  const t = limitar(tempo, 0, 1)
  const tabela = tabelaDoCaminho(tipo)

  // direção do trecho de caminho em que o veículo está agora
  const integralTotal = integralVelocidadeAte(tipo, 1)
  const percorrido =
    integralTotal < 1e-12 ? 0 : (integralVelocidadeAte(tipo, t) / integralTotal) * tabela.total
  let direcao = vetor(0, 0, 0)
  for (let i = 0; i < tabela.direcoes.length; i += 1) {
    if (percorrido <= tabela.acumulados[i + 1]! || i === tabela.direcoes.length - 1) {
      direcao = tabela.direcoes[i]!
      break
    }
  }
  const escalar = velocidadeRealMs(tipo, t, velocidadeKmh)
  // a escala do caminho já está embutida em `velocidadeRealMs`: ela é a razão
  // entre o comprimento esticado e a duração, que é exatamente a velocidade
  const translacao = vetor(direcao.x * escalar, direcao.y * escalar, direcao.z * escalar)

  const rolagem = rolagemEm(tipo, t)
  const inclinacao = inclinacaoLongitudinalEm(tipo, t)
  const omegaRoll = taxaDeRolagemRadPorS(tipo, t, velocidadeKmh)
  const omegaPitch = taxaDeInclinacaoLongitudinalRadPorS(tipo, t, velocidadeKmh)

  // Rz(rolagem) · Rx(inclinação): a rotação do teto/cofre também transfere
  // velocidade ao ocupante. Assim, o corpo inicia a integração no mesmo
  // referencial físico da carroceria inclinada, e não numa projeção plana.
  const aposPitch = girarEmTorno(pontoNoVeiculo, vetor(1, 0, 0), inclinacao)
  const girado = girarEmTorno(aposPitch, vetor(0, 0, 1), rolagem)
  const giroRoll = vetor(-omegaRoll * girado.y, omegaRoll * girado.x, 0)
  const giroPitchLocal = vetor(0, -omegaPitch * aposPitch.z, omegaPitch * aposPitch.y)
  const giroPitch = girarEmTorno(giroPitchLocal, vetor(0, 0, 1), rolagem)

  // A altura de apoio também muda enquanto a frente baixa. Derivada numérica
  // da própria curva de apoio evita separar novamente a pose da dinâmica.
  const duracao = duracaoRealS(tipo, velocidadeKmh)
  const passo = 1e-4
  const t0 = Math.max(0, t - passo)
  const t1 = Math.min(1, t + passo)
  const subida = duracao < 1e-12 || t1 === t0
    ? 0
    : (alturaDeApoio(perfil, rolagemEm(tipo, t1), inclinacaoLongitudinalEm(tipo, t1)) -
        alturaDeApoio(perfil, rolagemEm(tipo, t0), inclinacaoLongitudinalEm(tipo, t0))) /
      ((t1 - t0) * duracao)

  return vetor(
    translacao.x + giroRoll.x + giroPitch.x,
    translacao.y + giroRoll.y + giroPitch.y + subida,
    translacao.z + giroRoll.z + giroPitch.z,
  )
}

/** Amostras uniformes usadas para desenhar rastros de cabeça e tronco. */
export function amostrarIntervalo(
  tipo: TipoColisao,
  ate: number,
  quantidade = 28,
): QuadroCinematica[] {
  const limite = limitar(ate, 0, 1)
  const passos = Math.max(2, quantidade)
  return Array.from({ length: passos }, (_, i) =>
    amostrarCinematica(tipo, (limite * i) / (passos - 1)),
  )
}

export const VELOCIDADES_DISPONIVEIS = [0.25, 0.5, 1] as const
export type VelocidadeTimeline = (typeof VELOCIDADES_DISPONIVEIS)[number]
